/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { isString } from 'lodash-es';
import moment from './moment';

const urlParamsLocalStorageName = 'saved-url-params';
const urlParamsDateLocalStorageName = 'saved-url-params-date';
type URLParams = [string, string][];

export const getSavedURLParams = (): URLParams => {
  try {
    const urlParamsStoredDate = localStorage.getItem(
      urlParamsDateLocalStorageName
    );
    if (!urlParamsStoredDate || areUrlParamsOutdated(urlParamsStoredDate)) {
      removeSavedURLParams();
      return [];
    }
    const savedString = localStorage.getItem(urlParamsLocalStorageName);
    return isString(savedString) ? JSON.parse(savedString) : [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return [];
  }
};

export const setSavedURLParams = (params: URLParams) => {
  try {
    localStorage.setItem(urlParamsLocalStorageName, JSON.stringify(params));
    localStorage.setItem(urlParamsDateLocalStorageName, moment.utc().format());
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false;
  }
};

export const removeSavedURLParams = () => {
  try {
    localStorage.removeItem(urlParamsLocalStorageName);
    localStorage.removeItem(urlParamsDateLocalStorageName);
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false;
  }
};

export const removeSpecificSavedURLParams = (
  keysToRemove: string | string[]
) => {
  try {
    const currentParams = getSavedURLParams();
    if (currentParams.length === 0) {
      return true;
    }

    const keys = Array.isArray(keysToRemove) ? keysToRemove : [keysToRemove];

    const normalizedKeys = keys.map((key) =>
      key.startsWith('?') ? Array.from(new URLSearchParams(key).keys())[0] : key
    );

    const updatedParams = currentParams.filter(
      ([key]) => !normalizedKeys.includes(key)
    );

    if (updatedParams.length === currentParams.length) {
      return true;
    }

    if (updatedParams.length === 0) {
      return removeSavedURLParams();
    }

    return setSavedURLParams(updatedParams);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false;
  }
};

export const areUrlParamsOutdated = (dateStr: string) => {
  const date = moment(dateStr);
  return !date.isValid() || moment.utc().diff(date, 'days') >= 1;
};
